import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Checkbox,
  TablePagination,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Snackbar,
  alpha,
  FormControl,
  Select,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { MoreVertical, Plus, Package, Trash2, Edit } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts, deleteProduct, bulkDeleteProducts } from '../redux/slices/productSlice';
import { fetchCategories, fetchSubCategories, fetchCountries, fetchStates, fetchCities } from '../redux/slices/businessSlice';
import { fetchAdminBrands } from '../redux/slices/catalogSlice';
import ProductModal from '../components/ProductModal';
import PageHeader from '../components/common/PageHeader';
import FilterBar from '../components/common/FilterBar';
import StatusBadge from '../components/common/StatusBadge';
import EmptyState from '../components/common/EmptyState';
import { DeleteConfirmDialog, FilterDrawer, ImagePreviewDialog } from '../components/common/modal';
import { getProductImageUrl } from '../utils/helpers';
import { formatDateTime } from '../utils/formatDate';
import axios from '../utils/axios';
import ExportTableButton from '../components/common/ExportTableButton';

const ProductsView = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { products, totalProducts, isLoading, error } = useSelector((state) => state.product);

  const { categories, subCategories, countries, states, cities } = useSelector((state) => state.business);
  const { brands } = useSelector((state) => state.catalog);

  // Search & Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const activeFiltersCount = [categoryFilter, statusFilter, cityFilter, brandFilter].filter(Boolean).length;

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Modal & Row actions
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [actionProduct, setActionProduct] = useState(null);

  // Delete Confirm Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  // Image Preview Lightbox
  const [previewImage, setPreviewImage] = useState({ open: false, src: '', title: '', subtitle: '' });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadProducts = useCallback(() => {
    dispatch(fetchProducts({
      page: page + 1,
      limit: rowsPerPage,
      search: debouncedSearch,
      category: categoryFilter,
      city: cityFilter,
      status: statusFilter,
      brand: brandFilter
    }));
  }, [dispatch, page, rowsPerPage, debouncedSearch, categoryFilter, cityFilter, statusFilter, brandFilter]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchSubCategories());
    dispatch(fetchCountries());
    dispatch(fetchStates());
    dispatch(fetchCities());
    dispatch(fetchAdminBrands({ page: 1, limit: 1000, search: '' }));
  }, [dispatch]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = products.map((n) => n._id);
      setSelectedIds(newSelecteds);
      return;
    }
    setSelectedIds([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedIds.slice(1));
    } else if (selectedIndex === selectedIds.length - 1) {
      newSelected = newSelected.concat(selectedIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedIds.slice(0, selectedIndex),
        selectedIds.slice(selectedIndex + 1),
      );
    }
    setSelectedIds(newSelected);
  };

  const handleMenuOpen = (event, product) => {
    setMenuAnchorEl(event.currentTarget);
    setActionProduct(product);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActionProduct(null);
  };

  const handleEditClick = () => {
    setEditingProduct(actionProduct);
    setIsModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    // Only close the dropdown menu anchor — do NOT clear actionProduct yet.
    // actionProduct must remain set so confirmDelete can use it.
    setMenuAnchorEl(null);
    setIsBulkDelete(false);
    setDeleteDialogOpen(true);
  };

  const handleBulkDeleteClick = () => {
    setIsBulkDelete(true);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (isBulkDelete) {
        const result = await dispatch(bulkDeleteProducts(selectedIds));
        if (result.meta.requestStatus === 'fulfilled') {
          setSelectedIds([]);
          setSnackbar({ open: true, message: 'Selected products deleted successfully', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: result.payload || 'Failed to delete products', severity: 'error' });
        }
      } else if (actionProduct) {
        const result = await dispatch(deleteProduct(actionProduct._id));
        if (result.meta.requestStatus === 'fulfilled') {
          setSnackbar({ open: true, message: 'Product deleted successfully', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: result.payload || 'Failed to delete product', severity: 'error' });
        }
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'An unexpected error occurred', severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setActionProduct(null);
      loadProducts();
    }
  };

  const isSelected = (id) => selectedIds.indexOf(id) !== -1;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <PageHeader
        title="Products"
        subtitle="Manage product listings, stock statuses, categories and pricing"
        count={totalProducts}
        countLabel="products"
        action={
          <Button
            variant="contained"
            fullWidth={isMobile}
            startIcon={<Plus size={18} />}
            onClick={() => { setEditingProduct(null); setIsModalOpen(true); }}
          >
            New product
          </Button>
        }
      />

      <Card sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
        {isLoading && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6), zIndex: 10, backdropFilter: 'blur(2px)'
          }}>
            <CircularProgress thickness={4} size={36} sx={{ color: 'primary.main' }} />
          </Box>
        )}

        {/* Modernized FilterBar */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={(v) => setSearchTerm(v.trimStart())}
          searchPlaceholder="Search by name, brand or city..."
          selectedCount={selectedIds.length}
          onBulkDelete={handleBulkDeleteClick}
          onOpenFilter={() => setIsFilterOpen(true)}
          filterCount={activeFiltersCount}
          hasActiveFilters={Boolean(categoryFilter || statusFilter || cityFilter || brandFilter || searchTerm)}
          onResetFilters={() => {
            setCategoryFilter('');
            setStatusFilter('');
            setCityFilter('');
            setBrandFilter('');
            setSearchTerm('');
            setPage(0);
          }}
        >
          <ExportTableButton
            data={products}
            total={totalProducts}
            onExportAll={async () => {
              const res = await axios.get('/admin/products', {
                params: {
                  all: true,
                  search: debouncedSearch || undefined,
                  category: categoryFilter || undefined,
                  city: cityFilter || undefined,
                  status: statusFilter || undefined,
                  brand: brandFilter || undefined,
                },
              });
              return res.data.products || [];
            }}
            columns={[
              { label: 'Product Name', key: 'product_name' },
              { label: 'Brand', key: 'brand_name' },
              { label: 'Price', key: 'price' },
              { label: 'Category', value: (row) => typeof row.category === 'object' ? row.category?.category_name : row.category || '' },
              { label: 'City', key: 'city' },
              { label: 'Owner Name', value: (row) => `${row.owner?.first_name || row.user_id?.first_name || ''} ${row.owner?.last_name || row.user_id?.last_name || ''}`.trim() || '—' },
              { label: 'Owner Phone', value: (row) => row.owner?.phone || row.client_number || '—' },
              { label: 'Status', value: (row) => row.product_status || row.status || '' },
              { label: 'Created', key: 'createdAt' },
            ]}
            filename="products"
          />
        </FilterBar>

        {error && !isLoading ? (
          <Box sx={{ px: 3, pb: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 800 }}>

              <TableHead sx={{ backgroundColor: 'background.neutral' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={selectedIds.length > 0 && selectedIds.length < (products?.length || 0)}
                      checked={products?.length > 0 && selectedIds.length === products.length}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Product Name</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Brand</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Price</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Category & Location</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Owner</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Created</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {products && products.length > 0 ? products.map((product) => {
                  const isItemSelected = isSelected(product._id);
                  return (
                    <TableRow key={product._id} hover selected={isItemSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, product._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={getProductImageUrl(product.product_image)}
                            variant="rounded"
                            onClick={() => {
                              const imgUrl = getProductImageUrl(product.product_image);
                              if (imgUrl) {
                                setPreviewImage({
                                  open: true,
                                  src: imgUrl,
                                  title: product.product_name,
                                  subtitle: product.brand_name || 'Product Image',
                                });
                              }
                            }}
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 1.5,
                              bgcolor: 'background.neutral',
                              cursor: 'pointer',
                              transition: 'transform 0.15s ease',
                              '&:hover': { transform: 'scale(1.08)' },
                            }}
                          >
                            <Package size={24} color="#919EAB" />
                          </Avatar>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{product.product_name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{product.brand_name || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const catObj = categories?.find(c => c._id === product.category || c._id === product.category?._id);
                          const catName = catObj?.category_name || (typeof product.category === 'object' ? product.category?.category_name : product.category) || '';
                          const isService = String(catName).toLowerCase().includes('service');
                          const rawPrice = product.price !== undefined && product.price !== null ? String(product.price).trim() : '';
                          const numPrice = Number(rawPrice);
                          const hasValidPrice = rawPrice !== '' && !isNaN(numPrice) && numPrice > 0;

                          if (isService) {
                            if (hasValidPrice) {
                              return (
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    ₹{numPrice.toLocaleString('en-IN')}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem', fontWeight: 500 }}>
                                    Service Fee
                                  </Typography>
                                </Box>
                              );
                            }
                            return (
                              <Chip
                                label="Price on Request"
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  borderRadius: 1,
                                  backgroundColor: (theme) => alpha(theme.palette.info.main, 0.1),
                                  color: 'info.dark',
                                  border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                                }}
                              />
                            );
                          }

                          if (hasValidPrice) {
                            return (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                ₹{numPrice.toLocaleString('en-IN')}
                              </Typography>
                            );
                          }

                          return (
                            <Chip
                              label="Contact for Price"
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                borderRadius: 1,
                                backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.12),
                                color: 'text.secondary'
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{categories?.find(c => c._id === product.category)?.category_name || product.category}</Typography>
                        <Typography variant="caption" color="text.secondary">{product.city}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{product.owner ? `${product.owner.first_name} ${product.owner.last_name}` : 'N/A'}</Typography>
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={product.product_status === 'Activated' ? 'active' : product.product_status === 'Pending' ? 'pending' : 'inactive'}
                          label={product.product_status === 'Activated' ? 'In stock' : product.product_status === 'Pending' ? 'Low stock' : 'Out of stock'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap>{formatDateTime(product.createdAt || product.created_at)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton aria-label="Row actions" size="small" onClick={(e) => handleMenuOpen(e, product)}>
                          <MoreVertical size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                }) : !isLoading && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ p: 0 }}>
                      <EmptyState
                        icon={Package}
                        title="No products found"
                        description="Try adjusting your filters or search query to find product listings."
                        actionLabel="Add Product"
                        onAction={() => { setEditingProduct(null); setIsModalOpen(true); }}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 2 } }}>
          <TablePagination
            component="div"
            count={totalProducts || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "" : "Rows per page:"}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block'
              }
            }}
          />
        </Box>

      </Card>

      {/* Row Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        slotProps={{ paper: { sx: { width: 140 } } }}
      >
        <MenuItem onClick={handleEditClick} sx={{ fontSize: '0.875rem', gap: 1 }}>
          <Edit size={16} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main', fontSize: '0.875rem', gap: 1 }}>
          <Trash2 size={16} /> Delete
        </MenuItem>
      </Menu>

      {/* Filter Sidebar Drawer */}
      <FilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        title="Filter Products"
        subtitle="Filter marketplace inventory by category, status, brand, and city"
        activeCount={activeFiltersCount}
        onReset={() => {
          setCategoryFilter('');
          setStatusFilter('');
          setCityFilter('');
          setBrandFilter('');
          setPage(0);
        }}
        onApply={() => setIsFilterOpen(false)}
        isResetDisabled={!categoryFilter && !statusFilter && !cityFilter && !brandFilter}
      >
        {/* Category Filter */}
        <Box>
          <Typography variant="caption" fontWeight={800} sx={{ color: 'text.secondary', letterSpacing: 1, mb: 1.5, display: 'block' }}>CATEGORY</Typography>
          <FormControl fullWidth>
            <Select
              value={categoryFilter}
              displayEmpty
              onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
              sx={{ borderRadius: 2, bgcolor: 'background.neutral' }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories?.map((cat) => (
                <MenuItem key={cat._id} value={cat._id}>{cat.category_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider />

        {/* Brand Filter */}
        <Box>
          <Typography variant="caption" fontWeight={800} sx={{ color: 'text.secondary', letterSpacing: 1, mb: 1.5, display: 'block' }}>BRAND</Typography>
          <FormControl fullWidth>
            <Select
              value={brandFilter}
              displayEmpty
              onChange={(e) => { setBrandFilter(e.target.value); setPage(0); }}
              sx={{ borderRadius: 2, bgcolor: 'background.neutral' }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              <MenuItem value="">All Brands</MenuItem>
              {(brands?.data || []).map((b) => (
                <MenuItem key={b._id} value={b.brand_name}>{b.brand_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider />

        {/* Status Filter */}
        <Box>
          <Typography variant="caption" fontWeight={800} sx={{ color: 'text.secondary', letterSpacing: 1, mb: 1.5, display: 'block' }}>STATUS</Typography>
          <FormControl fullWidth>
            <Select
              value={statusFilter}
              displayEmpty
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              sx={{ borderRadius: 2, bgcolor: 'background.neutral' }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="Activated">In stock</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Deactivated">Out of stock</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider />

        {/* Region Filter */}
        <Box>
          <Typography variant="caption" fontWeight={800} sx={{ color: 'text.secondary', letterSpacing: 1, mb: 1.5, display: 'block' }}>LOCATION</Typography>
          <FormControl fullWidth>
            <Select
              value={cityFilter}
              displayEmpty
              onChange={(e) => { setCityFilter(e.target.value); setPage(0); }}
              sx={{ borderRadius: 2, bgcolor: 'background.neutral' }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              <MenuItem value="">All Cities</MenuItem>
              {cities?.map((c) => (
                <MenuItem key={c._id} value={c.city_name}>{c.city_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </FilterDrawer>

      {/* Product Create/Edit Modal */}
      <ProductModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        product={editingProduct}
        categories={categories}
        subCategories={subCategories}
        countries={countries}
        states={states}
        cities={cities}
        brands={brands?.data || []}
        onSuccess={() => {
          loadProducts();
          setSnackbar({ open: true, message: `Product ${editingProduct ? 'updated' : 'created'} successfully!`, severity: 'success' });
        }}
      />

      {/* Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDelete={confirmDelete}
        title={isBulkDelete ? `Delete ${selectedIds.length} Products?` : `Delete "${actionProduct?.product_name || 'Product'}"?`}
        itemName={isBulkDelete ? `${selectedIds.length} products` : actionProduct?.product_name}
        itemType={isBulkDelete ? 'products' : 'product listing'}
        description={
          isBulkDelete
            ? `Are you sure you want to permanently delete ${selectedIds.length} selected products?`
            : `Are you sure you want to permanently delete "${actionProduct?.product_name || 'this product'}"?`
        }
        confirmText={isBulkDelete ? `Delete ${selectedIds.length} Products` : "Delete Product"}
      />

      {/* Image Preview Lightbox Modal */}
      <ImagePreviewDialog
        open={previewImage.open}
        onClose={() => setPreviewImage({ ...previewImage, open: false })}
        src={previewImage.src}
        title={previewImage.title}
        subtitle={previewImage.subtitle}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2, boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductsView;
